import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import MasterLayout from "../masterLayout/MasterLayout";
import Breadcrumb from "../components/Breadcrumb";
import KkpPlanningPageActions from "../components/kkp/KkpPlanningPageActions";
import KkpPlanningEmptyState from "../components/kkp/KkpPlanningEmptyState";
import apiService from "../services/apiService";
import { ROUTES } from "../config/routes";
import { 
  getRenewalState, 
  getDomainStatus, 
  getDaysUntilExpiration,
  getRenewalStateClass,
  getRenewalStateText,
  getStatusClass,
  getStatusText,
  getDaysRemainingClass,
  getDaysRemainingText
} from "../utils/domainUtils";
import { AppAlert } from "../components/ui";
import "../styles/kkp-planning-page.scss";

const DomaineDetailOVHPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [domain, setDomain] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [apiWorking, setApiWorking] = useState(true);

  // Fonction pour formater la date
  const formatDate = useCallback((dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('fr-FR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  }, []);


  // Charger les détails du domaine
  useEffect(() => {
    const loadDomainDetails = async () => {
      try {
        setLoading(true);
        setError(null);

        if (!apiService.isConfigured()) {
          setError('Service API non configuré');
          setLoading(false);
          return;
        }

        const domainData = await apiService.getDomainById(parseInt(id));
        setDomain(domainData);
        setApiWorking(true);

      } catch (err) {
        console.error('Erreur lors du chargement du domaine:', err);
        setError(err.message || 'Erreur lors du chargement du domaine');
        setApiWorking(false);
        
        // Données de test en cas d'erreur
        const testDomain = {
          id: parseInt(id),
          name: `test-domain-${id}.com`,
          status: 'ok',
          expirationDate: '2025-12-31T00:00:00Z',
          autoRenew: true,
          details: {
            offer: 'gold',
            dnssecState: 'enabled',
            nameServers: [
              { nameServer: 'ns1.ovh.net', nameServerType: 'hosting' },
              { nameServer: 'dns1.ovh.net', nameServerType: 'hosting' }
            ],
            lastUpdate: '2025-10-08T10:00:00Z',
            contactTech: { id: 'aj211-ovh' },
            contactAdmin: { id: 'aj211-ovh' },
            contactBilling: { id: 'aj211-ovh' },
            contactOwner: { id: '1234567' },
            whoisOwner: '1234567',
            renewalState: 'automatic_renew',
            transferLockStatus: 'locked',
            suspensionState: 'not_suspended'
          },
          contacts: [],
          dns: [1, 2],
          renewal: {
            renew: {
              forced: false,
              period: 12,
              automatic: true,
              manualPayment: false,
              deleteAtExpiration: false
            },
            status: 'ok',
            creation: '2020-01-01',
            expiration: '2025-12-31',
            renewalType: 'automaticV2016',
            possibleRenewPeriod: [12, 24, 36, 48, 60, 72, 84, 96, 108],
            canDeleteAtExpiration: true
          }
        };
        setDomain(testDomain);
      } finally {
        setLoading(false);
      }
    };

    if (id) {
      loadDomainDetails();
    }
  }, [id]);

  // Calculer les informations dérivées
  const domainInfo = useMemo(() => {
    if (!domain) return null;

    const status = getDomainStatus(domain);
    const days = getDaysUntilExpiration(domain.expirationDate);
    const renewalState = getRenewalState(domain);
    
    return {
      ...domain,
      status,
      daysRemaining: days,
      renewalState,
      statusClass: getStatusClass(status),
      statusText: getStatusText(status),
      daysClass: getDaysRemainingClass(days),
      daysText: getDaysRemainingText(days),
      renewalClass: getRenewalStateClass(renewalState),
      renewalText: getRenewalStateText(renewalState)
    };
  }, [domain, getDomainStatus, getDaysUntilExpiration, getRenewalState, getStatusClass, getStatusText, getDaysRemainingClass, getDaysRemainingText, getRenewalStateClass, getRenewalStateText]);

  const backToList = () => navigate(ROUTES.iti.domainesOvh);

  const toolsMenu = (
    <KkpPlanningPageActions
      actions={[
        {
          key: 'back',
          label: 'Retour à la liste',
          icon: 'mdi:arrow-left',
          onClick: backToList,
        },
      ]}
    />
  );

  if (loading) {
    return (
      <MasterLayout>
        <Breadcrumb
          title="Domaine OVH"
          subtitle="Détail domaine OVH"
          actions={toolsMenu}
        />
        <KkpPlanningEmptyState loading />
      </MasterLayout>
    );
  }

  if (error && !domain) {
    return (
      <MasterLayout>
        <Breadcrumb
          title="Domaine OVH"
          subtitle="Détail domaine OVH"
          actions={toolsMenu}
        />
        <KkpPlanningEmptyState
          message={error}
          actions={[
            {
              key: 'back',
              label: 'Retour à la liste',
              icon: 'mdi:arrow-left',
              onClick: backToList,
              variant: 'primary',
            },
          ]}
        />
      </MasterLayout>
    );
  }

  if (!domainInfo) {
    return (
      <MasterLayout>
        <Breadcrumb
          title="Domaine OVH"
          subtitle="Détail domaine OVH"
          actions={toolsMenu}
        />
        <KkpPlanningEmptyState
          message="Domaine non trouvé"
          actions={[
            {
              key: 'back',
              label: 'Retour à la liste',
              icon: 'mdi:arrow-left',
              onClick: backToList,
              variant: 'primary',
            },
          ]}
        />
      </MasterLayout>
    );
  }

  return (
    <MasterLayout>
      <Breadcrumb
        title={domainInfo.name}
        subtitle={
          <>
            Détail domaine OVH
            {!apiWorking && (
              <span className="badge bg-warning ms-2">Mode test</span>
            )}
          </>
        }
        actions={toolsMenu}
      />

      {error && (
        <AppAlert variant="warning" className="py-2">
          <strong>Mode test :</strong> {error}
        </AppAlert>
      )}

      <div className="row">
          {/* Informations générales */}
          <div className="col-lg-8">
            <div className="card mb-4">
              <div className="card-header py-2">
                <h5 className="card-title mb-0 text-sm">
                  <i className="ri-information-line me-2"></i>
                  Informations générales
                </h5>
              </div>
              <div className="card-body">
                <div className="row">
                  <div className="col-md-6">
                    <div className="mb-3">
                      <label className="form-label fw-bold">Nom de domaine</label>
                      <p className="form-control-plaintext">{domainInfo.name}</p>
                    </div>
                    <div className="mb-3">
                      <label className="form-label fw-bold">Statut</label>
                      <div>
                        <span className={`badge ${domainInfo.statusClass}`}>
                          {domainInfo.statusText}
                        </span>
                      </div>
                    </div>
                    <div className="mb-3">
                      <label className="form-label fw-bold">Jours restants</label>
                      <div>
                        <span className={`badge ${domainInfo.daysClass}`}>
                          {domainInfo.daysText}
                        </span>
                      </div>
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="mb-3">
                      <label className="form-label fw-bold">Date d'expiration</label>
                      <p className="form-control-plaintext">
                        {formatDate(domainInfo.expirationDate)}
                      </p>
                    </div>
                    <div className="mb-3">
                      <label className="form-label fw-bold">État du renouvellement</label>
                      <div>
                        <span className={`badge ${domainInfo.renewalClass}`}>
                          {domainInfo.renewalText}
                        </span>
                      </div>
                    </div>
                    <div className="mb-3">
                      <label className="form-label fw-bold">Offre</label>
                      <p className="form-control-plaintext">
                        <span className="badge bg-info">
                          {domainInfo.details?.offer || 'N/A'}
                        </span>
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Serveurs DNS */}
            <div className="card mb-4">
              <div className="card-header py-2">
                <h5 className="card-title mb-0 text-sm">
                  <i className="ri-dns-line me-2"></i>
                  Serveurs DNS
                </h5>
              </div>
              <div className="card-body">
                {domainInfo.details?.nameServers ? (
                  <div className="row">
                    {domainInfo.details.nameServers.map((ns, index) => (
                      <div key={index} className="col-md-6 mb-2">
                        <div className="d-flex align-items-center">
                          <i className="ri-server-line me-2 text-primary"></i>
                          <span className="fw-bold me-2">{ns.nameServer}</span>
                          <span className="badge bg-light text-dark">{ns.nameServerType}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-muted">Aucun serveur DNS configuré</p>
                )}
              </div>
            </div>

            {/* Informations de renouvellement */}
            <div className="card mb-4">
              <div className="card-header py-2">
                <h5 className="card-title mb-0 text-sm">
                  <i className="ri-refresh-line me-2"></i>
                  Informations de renouvellement
                </h5>
              </div>
              <div className="card-body">
                {domainInfo.renewal ? (
                  <div className="row">
                    <div className="col-md-6">
                      <div className="mb-3">
                        <label className="form-label fw-bold">Période de renouvellement</label>
                        <p className="form-control-plaintext">
                          {domainInfo.renewal.renew?.period || 'N/A'} mois
                        </p>
                      </div>
                      <div className="mb-3">
                        <label className="form-label fw-bold">Type de renouvellement</label>
                        <p className="form-control-plaintext">
                          {domainInfo.renewal.renewalType || 'N/A'}
                        </p>
                      </div>
                    </div>
                    <div className="col-md-6">
                      <div className="mb-3">
                        <label className="form-label fw-bold">Date de création</label>
                        <p className="form-control-plaintext">
                          {formatDate(domainInfo.renewal.creation)}
                        </p>
                      </div>
                      <div className="mb-3">
                        <label className="form-label fw-bold">Périodes possibles</label>
                        <p className="form-control-plaintext">
                          {domainInfo.renewal.possibleRenewPeriod?.join(', ') || 'N/A'} mois
                        </p>
                      </div>
                    </div>
                  </div>
                ) : (
                  <p className="text-muted">Aucune information de renouvellement disponible</p>
                )}
              </div>
            </div>
          </div>

          {/* Informations techniques */}
          <div className="col-lg-4">
            <div className="card mb-4">
              <div className="card-header py-2">
                <h5 className="card-title mb-0 text-sm">
                  <i className="ri-settings-3-line me-2"></i>
                  Informations techniques
                </h5>
              </div>
              <div className="card-body">
                <div className="mb-3">
                  <label className="form-label fw-bold">DNSSEC</label>
                  <div>
                    <span className={`badge ${domainInfo.details?.dnssecState === 'enabled' ? 'bg-success' : 'bg-secondary'}`}>
                      {domainInfo.details?.dnssecState === 'enabled' ? 'Activé' : 'Désactivé'}
                    </span>
                  </div>
                </div>
                <div className="mb-3">
                  <label className="form-label fw-bold">État de suspension</label>
                  <div>
                    <span className={`badge ${domainInfo.details?.suspensionState === 'not_suspended' ? 'bg-success' : 'bg-danger'}`}>
                      {domainInfo.details?.suspensionState === 'not_suspended' ? 'Non suspendu' : 'Suspendu'}
                    </span>
                  </div>
                </div>
                <div className="mb-3">
                  <label className="form-label fw-bold">Verrouillage de transfert</label>
                  <div>
                    <span className={`badge ${domainInfo.details?.transferLockStatus === 'locked' ? 'bg-warning' : 'bg-success'}`}>
                      {domainInfo.details?.transferLockStatus === 'locked' ? 'Verrouillé' : 'Déverrouillé'}
                    </span>
                  </div>
                </div>
                <div className="mb-3">
                  <label className="form-label fw-bold">Dernière mise à jour</label>
                  <p className="form-control-plaintext">
                    {formatDate(domainInfo.details?.lastUpdate)}
                  </p>
                </div>
              </div>
            </div>

            {/* Contacts */}
            <div className="card mb-4">
              <div className="card-header py-2">
                <h5 className="card-title mb-0 text-sm">
                  <i className="ri-contacts-line me-2"></i>
                  Contacts
                </h5>
              </div>
              <div className="card-body">
                <div className="mb-3">
                  <label className="form-label fw-bold">Propriétaire</label>
                  <p className="form-control-plaintext">
                    {domainInfo.details?.contactOwner?.id || 'N/A'}
                  </p>
                </div>
                <div className="mb-3">
                  <label className="form-label fw-bold">Administrateur</label>
                  <p className="form-control-plaintext">
                    {domainInfo.details?.contactAdmin?.id || 'N/A'}
                  </p>
                </div>
                <div className="mb-3">
                  <label className="form-label fw-bold">Technique</label>
                  <p className="form-control-plaintext">
                    {domainInfo.details?.contactTech?.id || 'N/A'}
                  </p>
                </div>
                <div className="mb-3">
                  <label className="form-label fw-bold">Facturation</label>
                  <p className="form-control-plaintext">
                    {domainInfo.details?.contactBilling?.id || 'N/A'}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
    </MasterLayout>
  );
};

export default DomaineDetailOVHPage;
